import React, { Fragment, useEffect, useMemo, useState } from "react";
import "./KYC-PrimaryContact.css";
import { Breadcrumb, Button, Card, Spin } from "antd";
import {
	ApartmentOutlined,
	DoubleLeftOutlined,
	EnvironmentOutlined,
	GlobalOutlined,
	MailOutlined,
	PhoneOutlined,
	PushpinOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import KycService from "../../services/kyc-service";
import { buildKycCompanyTabUrl } from "./kycHelpers";

const getValue = (obj, keys) => {
	for (const key of keys) {
		const value = obj?.[key];
		if (
			value !== undefined &&
			value !== null &&
			value !== "" &&
			value !== "Microsoft.Xrm.Sdk.OptionSetValue"
		) {
			return value;
		}
	}

	return "-";
};

const buildAddressLine = (data) => {
	const parts = [
		getValue(data, ["address1_Line1", "address1_line1", "address1"]),
		getValue(data, ["address1_Line2", "address1_line2", "address2"]),
		getValue(data, ["address1_Line3", "address1_county", "area", "areaName"]),
		getValue(data, ["ispl_City", "address1_city", "city"]),
	].filter((part) => part && part !== "-");

	return parts.length ? parts.join(", ") : "-";
};

const KycQuotationContactDetails = () => {
	const { customerId } = useParams();
	const location = useLocation();
	const [isLoading, setIsLoading] = useState(false);
	const [primaryContactData, setPrimaryContactData] = useState({});

	const quotationNumber = location.state?.quotationNumber || "";
	const dashboardQuotationTabLink = buildKycCompanyTabUrl("quotation");

	useEffect(() => {
		if (!customerId) {
			return;
		}

		setIsLoading(true);
		KycService.getQuotationContactDataByIdAPI({
			customerId: Number(customerId) || customerId,
		})
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data?.responseData;
					const contactList = Array.isArray(responseData)
						? responseData
						: responseData?.contactDataLists ||
							responseData?.contactDataList ||
							responseData?.contactData ||
							responseData?.data ||
							[];
					const finalData = Array.isArray(contactList)
						? contactList[0] || {}
						: contactList || responseData || {};
					setPrimaryContactData(finalData);
				} else {
					NotificationManager.error(response.data?.message || "Unable to load contact details.");
					setPrimaryContactData({});
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load contact details.");
				setPrimaryContactData({});
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [customerId]);

	const contactDisplay = useMemo(() => {
		const firstName = getValue(primaryContactData, ["firstName", "firstname", "ispl_FirstName"]);
		const lastName = getValue(primaryContactData, ["lastName", "lastname", "ispl_LastName"]);
		const fullName =
			firstName !== "-" && lastName !== "-"
				? `${firstName} ${lastName}`.trim()
				: firstName !== "-"
					? firstName
					: lastName !== "-"
						? lastName
						: "-";

		const avatarInitial = fullName !== "-" ? fullName.trim().charAt(0).toUpperCase() : "?";

		return {
			displayName: fullName !== "-" ? fullName.toUpperCase() : "-",
			avatarInitial,
			email: getValue(primaryContactData, ["email", "emailaddress1", "primaryEmail"]),
			phone: getValue(primaryContactData, ["mobilePhone", "mobilephone", "telephone1", "phone"]),
			addressLine: buildAddressLine(primaryContactData),
			geoItems: [
				{
					key: "country",
					label: "Country",
					value: getValue(primaryContactData, ["ispl_Country", "address1_country", "country", "countryName"]),
					icon: <GlobalOutlined />,
				},
				{
					key: "area",
					label: "Area",
					value: getValue(primaryContactData, ["address1_Line3", "address1_county", "area", "areaName", "ispl_Area"]),
					icon: <PushpinOutlined />,
				},
				{
					key: "state",
					label: "State",
					value: getValue(primaryContactData, ["ispl_State", "address1_stateorprovince", "state", "stateName"]),
					icon: <EnvironmentOutlined />,
				},
				{
					key: "postalCode",
					label: "Postal Code",
					value: getValue(primaryContactData, ["ispl_PostCode", "address1_postalcode", "postalCode", "zipcode"]),
					icon: <ApartmentOutlined />,
				},
			],
		};
	}, [primaryContactData]);

	const primaryContactBreadcrumbLabel = useMemo(() => {
		const stateName = location.state?.primaryContactName;
		if (stateName !== undefined && stateName !== null && String(stateName).trim() !== "") {
			return String(stateName).trim();
		}
		if (contactDisplay.displayName && contactDisplay.displayName !== "-") {
			return contactDisplay.displayName;
		}
		return "Primary Contact";
	}, [location.state?.primaryContactName, contactDisplay.displayName]);

	return (
		<Fragment>
			<div className="dashboard app-content content">
				<div className="content-wrapper">
					<div className="content-body">
						<div className="d-flex justify-content-between align-items-center details_view_title mb-2">
							<Breadcrumb className="details_breadcrum" separator=">">
								<Breadcrumb.Item>
									<Link to="/kyc">Dashboard</Link>
								</Breadcrumb.Item>
								<Breadcrumb.Item>
									<Link to={dashboardQuotationTabLink}>
										<label>Quotation</label>
									</Link>
								</Breadcrumb.Item>
								{quotationNumber ? (
									<Breadcrumb.Item>
										<label>{quotationNumber}</label>
									</Breadcrumb.Item>
								) : null}
								<Breadcrumb.Item>
									<label>{primaryContactBreadcrumbLabel}</label>
								</Breadcrumb.Item>
							</Breadcrumb>
							<Link to={dashboardQuotationTabLink}>
								<Button type="primary back_btn" icon={<DoubleLeftOutlined />}>
									Back
								</Button>
							</Link>
						</div>
						<Spin size="large" spinning={isLoading}>
							<Card className="kyc-primary-details-card">
								<div className="kyc-primary-contact-panel">
									<div className="kyc-primary-contact-panel__head">
										<span className="kyc-primary-contact-panel__head-icon" aria-hidden>
											<UserOutlined />
										</span>
										<h3>Primary Contact Details</h3>
									</div>

									<div className="kyc-primary-contact-profile">
										<div
											className="kyc-primary-contact-avatar"
											aria-hidden={contactDisplay.displayName === "-"}
										>
											{contactDisplay.avatarInitial}
										</div>
										<div className="kyc-primary-contact-profile__body">
											<h4>{contactDisplay.displayName}</h4>
											<div className="kyc-primary-contact-chips">
												<div className="kyc-primary-contact-chip">
													<span
														className="kyc-primary-contact-chip__icon kyc-primary-contact-chip__icon--email"
														aria-hidden
													>
														<MailOutlined />
													</span>
													{contactDisplay.email !== "-" ? (
														<a
															className="kyc-primary-contact-chip__text"
															href={`mailto:${contactDisplay.email}`}
														>
															{contactDisplay.email}
														</a>
													) : (
														<span className="kyc-primary-contact-chip__text">
															{contactDisplay.email}
														</span>
													)}
												</div>
												<div className="kyc-primary-contact-chip">
													<span
														className="kyc-primary-contact-chip__icon kyc-primary-contact-chip__icon--phone"
														aria-hidden
													>
														<PhoneOutlined />
													</span>
													{contactDisplay.phone !== "-" ? (
														<a
															className="kyc-primary-contact-chip__text"
															href={`tel:${contactDisplay.phone}`}
														>
															{contactDisplay.phone}
														</a>
													) : (
														<span className="kyc-primary-contact-chip__text">
															{contactDisplay.phone}
														</span>
													)}
												</div>
											</div>
										</div>
									</div>

									<div className="kyc-primary-contact-section">
										<div className="kyc-primary-contact-section__label">
											<span className="kyc-primary-contact-address__icon" aria-hidden>
												<EnvironmentOutlined />
											</span>
											Address
										</div>
										<div className="kyc-primary-contact-address">
											<span className="kyc-primary-contact-address__icon" aria-hidden>
												<ApartmentOutlined />
											</span>
											<p>{contactDisplay.addressLine}</p>
										</div>
									</div>

									<div className="kyc-primary-contact-geo">
										{contactDisplay.geoItems.map((item) => (
											<div className="kyc-primary-contact-geo-item" key={item.key}>
												<span className="kyc-primary-contact-geo-item__icon" aria-hidden>
													{item.icon}
												</span>
												<div className="kyc-primary-contact-geo-item__content">
													<label>{item.label}</label>
													<p>{item.value}</p>
												</div>
											</div>
										))}
									</div>
								</div>
							</Card>
						</Spin>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default KycQuotationContactDetails;
